import React, { useState } from 'react'
import { useController, Control, FieldValues, PathValue, Path, UseControllerProps } from 'react-hook-form'
import useAsyncService from '../../hooks/useAsyncService'
import { loadFleets } from '../../services/APIServices'
import './ICSVehiclePicker.css'
import { _DataSearchValues } from '../../pages/Data/Search/SearchBox'
import { vehicle } from 'faker'

interface Vehicle {
  id: string
  name: string
}

interface Fleet {
  id: string
  name: string
  vehicles: Vehicle[]
}

export type VehiclePickerValue = {
  vehicles: string[];
  fleets: string[];
}

//Note - There is a type issue that is not well documented with RHF where useControllerProps
// and similar don't resolve value to the proper type. This ensures type safety. There is
// talk about fixing it in v8, but that's been a couple years... SEV
export const ensureVehiclePickerValue = (value: unknown): VehiclePickerValue => {
    if (
        value !== null &&
        typeof value === 'object' &&
        Array.isArray((value as any).vehicles) &&
        Array.isArray((value as any).fleets)
    ) {
        return value as VehiclePickerValue
    }

    return { vehicles: [], fleets: [] }
}

//export type EnsureVehiclePicker<T, K> = PathValue<T, K extends Path<T> ? Path<T> : never> extends Record<K extends string ? K : never, VehiclePickerValue> ? Record<K extends string ? K : never, VehiclePickerValue>  : never
export type EnsureVehiclePicker<V> = V extends Record<string, VehiclePickerValue> ? V : never

export type VehiclePickerProps<T extends FieldValues, K extends Path<T>> = {
  name: K;
  control: Control<T>;
}//& EnsureVehiclePicker<PathValue<T, K>>


const ICSVehiclePicker = <T extends _DataSearchValues, K extends Path<T>>({
  name,
  control,
}: UseControllerProps<T, K>) => {

  const [searchTerm, setSearchTerm] = useState('')
  const [expandList, setExpandList] = useState(false);
  const { serviceLoading, serviceValue: fleets } = useAsyncService<void, Fleet[]>({
    serviceCall: loadFleets,
    delay: 0,
    initialArgs: undefined
  })

  const {
    field: { value, onChange }
  } = useController({
    name,
    control,
    rules: {
      validate: {
        required: (v) => {
          const val = ensureVehiclePickerValue(v)
          if(val.fleets.length < 1 && val.vehicles.length < 1) {
            return "Required"
          }
        }
      }
    }
  })

  //Initialize while waiting for useController hook to kick in.
  const { fleets: selectedFleets, vehicles: selectedVehicles } = ensureVehiclePickerValue(value)

  // Filtered fleets and vehicles based on the search term
  const filteredFleets = fleets === null ? [] : fleets
    .map((fleet) => {
      const matchingVehicles = fleet.vehicles.filter((vehicle) =>
        vehicle.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      const isFleetMatching = fleet.name.toLowerCase().includes(searchTerm.toLowerCase())

      if (isFleetMatching || matchingVehicles.length > 0) {
        return {
          ...fleet,
          vehicles: isFleetMatching ? fleet.vehicles : matchingVehicles
        }
      }

      return null
    })
    .filter((fleet): fleet is Fleet => fleet !== null)

  // Handle fleet selection
  const handleFleetChange = (fleetId: string, vehicles: Vehicle[]) => {
    const allVehicleIds = vehicles.map((v) => v.id)
    const isSelected = selectedFleets.indexOf(fleetId) !== -1

    // Keep vehicles set to current selectedVehicles to persist selections
    if (isSelected) {
      onChange({
        fleets: selectedFleets.filter((id) => id !== fleetId),
        vehicles: selectedVehicles
      })
    } else {
      onChange({
        fleets: selectedFleets.concat([fleetId]),
        vehicles: selectedVehicles
      })
    }
  }

  // Handle vehicle selection
  const handleVehicleChange = (fleetId: string, vehicleId: string) => {
    if(!fleets) return

    const fleet = fleets.find((f) => f.id === fleetId)
    const fleetVehicleIds = fleet ? fleet.vehicles.map((v) => v.id)  : []

    if(selectedFleets.indexOf(fleetId) === -1){
      const newSelectedVehicles = selectedVehicles.indexOf(vehicleId) !== -1
        ? selectedVehicles.filter((vid) => vid !== vehicleId)
        : selectedVehicles.concat([vehicleId])

      const isAllVehiclesSelected = fleetVehicleIds.every((vid) =>
        newSelectedVehicles.indexOf(vid) !== -1
      )
     const newSelectedFleets = isAllVehiclesSelected
      ? Array.from(new Set(selectedFleets.concat([fleetId])))
      : selectedFleets.filter((id) => id !== fleetId)
      onChange({ fleets: newSelectedFleets, vehicles: newSelectedVehicles })
    }
    else{
      const newSelectedVehicles:string[] = []
      fleetVehicleIds.forEach((vehicleIds)=> {if(vehicleIds !== vehicleId) {newSelectedVehicles.push(vehicleIds)}} )

      const newSelectedFleets = selectedFleets.filter((id) => id !== fleetId)
      onChange({ fleets: newSelectedFleets, vehicles: newSelectedVehicles })
    }
  }

  // Clear all selections
  const clearSelections = () => {
    onChange({ fleets: [], vehicles: [] })
  }

  // Select all fleets and vehicles
  const selectAll = () => {
    if(!fleets) return

    const allFleets = fleets.map((fleet) => fleet.id)
    onChange({ fleets: allFleets, vehicles: [] })
  }

  // const selectedCount = selectedFleets.reduce((a,b) => {
  //   if(b.selected) {
  //     a += fleets![b].vehicles.length
  //   }

  //   return a
  // }, 0) + selectedVehicles.length
  //let selectedCount = 0


  let fleetVehicles = 0;
  const vehicleArr:Vehicle[] = [];
  let vehicleIds:string[] = [];
  const selectedCount = () => {
    return fleets?.reduce((a, b) => {
      if(selectedFleets.indexOf(b.id) !== -1) {   
        vehicleArr.push(...b.vehicles)
        fleetVehicles = b.vehicles.length + selectedVehicles.length;
        a += fleetVehicles
        if(vehicleArr.length){
          vehicleIds = [...vehicleIds, ...vehicleArr.map((x) => x.id)]
        }
      }
      if(selectedVehicles.length){
        selectedVehicles.forEach((x) => {vehicleIds.push(x)});
      }
      a = vehicleIds.filter((item, index) => vehicleIds.indexOf(item) == index).length
      return a
    }, 0)
  }

  return (
    <>
      {/* Title and Expand List */}
      <div className="picker-header">
        <h3>Fleets/Vehicles</h3>
        <div style={{fontSize: "12px", height: "auto"}} className="expand-picker-list" aria-label="expandlist" tabIndex={0} role="button" onClick={(e) => {e.preventDefault(); setExpandList(!expandList)}}>
            <i className={expandList ? "dx-icon dx-icon-minus" : "dx-icon dx-icon-plus"}></i>
            <span className="expand-list-tooltip">Expand or Retract Fleets/Vehicles List</span>
        </div>
      </div>
      <div className="ics-vehicle-picker">
        {/* Selection Status and Select Action Buttons */}
        <div className="picker-status">
          <span className="picker-selected-count">{selectedCount()} vehicles selected</span>
          <a className="picker-clear-btn" href="#" onClick={(e) => { e.preventDefault(); clearSelections() }}>
            Clear
          </a>
          <a className="picker-select-all" href="#" onClick={(e) => { e.preventDefault(); selectAll() }}>
            Select All
          </a>
          {/* Search Fleet/Vehicle List */}
          <div className="picker-search">
            <input
              type="text"
              placeholder="Search Fleets/Vehicles"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="picker-clear-search dx-ixon dx-icon-clear" onClick={(e) => {e.preventDefault(); setSearchTerm("")}}/>
          </div>
        </div>

        {/* Scrollable Fleet and Vehicle List */}
        <div className={expandList ? "picker-list expanded-picker-list" : "picker-list"}>
          {serviceLoading || fleets === null ? (
            <div className="loading">Loading fleets...</div>
          ) : !fleets || fleets.length === 0 ? (
            <div className="no-fleets">There are no fleets available.</div>
          ) : (
            filteredFleets.map((fleet) => (
              <div key={fleet.id} className="fleet-section">
                <label>
                  <input
                    type="checkbox"
                    checked={selectedFleets.indexOf(fleet.id) !== -1}
                    onChange={() => handleFleetChange(fleet.id, fleet.vehicles)}
                  />
                  {fleet.name}
                </label>
                <div className="vehicle-list">
                  {fleet.vehicles.map((vehicle) => (
                    <div key={vehicle.id} className="vehicle-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={selectedVehicles.indexOf(vehicle.id) !== -1 ||  selectedFleets.indexOf(fleet.id) !== -1}
                          onChange={() => {handleVehicleChange(fleet.id, vehicle.id)}}
                        />
                        {vehicle.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  )
}

export default ICSVehiclePicker
